import { IControl, IValidationControl, ValidationCallback } from '@Common/Controls/Controls';
import { GridLayout, IGridLayoutParameters } from '@Common/Controls/GridLayout';
import { IDelegate, IMulticastDelegate, MulticastDelegateBag, SubscriberBag } from '@Common/Core/MulticastDelegate';
import { ScrollablePanel } from './ScrollablePanel';
import { ISpinnerContainer, ISpinnerSetting } from '@Common/Plugins/spinner';

export abstract class BaseCompositeControl implements IControl, IValidationControl, ISpinnerContainer {
    private _isValid = true;
    protected root!: GridLayout;
    protected readonly subscribers = new SubscriberBag();
    protected readonly eventBags = new MulticastDelegateBag();
    public readonly onValidate = this.eventBags.create<ValidationCallback>();
    private states: IMap<boolean> = {};
    private outer: IControl | undefined;

    constructor(create?: () => void, private readonly vScroll = false, private readonly hScroll = false) {
        if (create) {
            create();
            this.createRoot();
        }
    }

    public spinnerStart(param?: ISpinnerSetting): void {
        this.root.spinnerStart(param);
    }

    public spinnerStop(): void {
        this.root.spinnerStop();
    }

    protected async suppressEvents(delegates: IMulticastDelegate<IDelegate>[], fn: IHandler): Promise<void> {
        try {
            delegates.forEach(f => {
                f.enabled = false;
            });
            await fn();
        } finally {
            delegates.forEach(f => {
                f.enabled = true;
            });
        }
    }

    public validate(): boolean {
        this._isValid = this.validateInner();
        this.onValidate(this._isValid);
        return this._isValid;
    }

    protected validateInner(): boolean {
        return true;
    }

    public get isValid(): boolean {
        return this._isValid;
    }

    public getControl(): JQuery {
        this.checkInvariant();
        return this.outer.getControl();
    }

    public setState(state: boolean): void {
        this.checkInvariant();
        this.outer.enabled(state);
    }

    public adjust(w?: number, h?: number): void {
        this.checkInvariant();
        this.outer.adjust(w, h);
    }

    public enabled(val?: boolean): boolean {
        return this.stateStore(state => this.outer.enabled(state), val, true);
    }

    public visible(val?: boolean): boolean {
        return this.stateStore(state => this.outer.visible(state), val, true);
    }

    public readonly(val?: boolean): boolean {
        return this.stateStore(state => this.outer.readonly(state), val, false);
    }

    public destroy(): void {
        this.subscribers.destroy();
        this.eventBags.destroy();
        this.outer?.destroy?.();
        this.outer = undefined;
    }

    protected abstract initLayout(): IGridLayoutParameters;

    protected createRoot(): void {
        this.root = new GridLayout(_.defaults(this.initLayout(), { id: _.uniqueId(this.constructor.name) }));
        if (this.hScroll || this.vScroll) {
            const scroll = (this.outer = new ScrollablePanel({ hScroll: this.hScroll, vScroll: this.vScroll }));
            scroll.append(this.root);
        } else {
            this.outer = this.root;
        }
    }

    protected setOuter(outer: IControl): void {
        this.outer = outer;
    }

    private stateStore(fn: (state: boolean) => void, state: boolean, defaultState: boolean): boolean {
        // BUG: GridLayout don't return currentState
        const stateId = fn.toString();
        if (typeof state === typeof void 0) {
            return this.states[stateId] != null ? this.states[stateId] : defaultState;
        } else {
            if (this.states[stateId] !== state) {
                this.states[stateId] = state;
                this.checkInvariant();
                fn(state);
            }
            return state;
        }
    }

    private checkInvariant(): void {
        if (this.outer == null) {
            throw new Error('Before using you must call BaseCompositeControl::createRoot or It is destroyed');
        }
    }

    public static create<TCtor extends new (...args: any[]) => R, R extends BaseCompositeControl = InstanceType<TCtor>>(
        this: TCtor,
        ...args: ConstructorParameters<TCtor>
    ): R {
        const ctrl = new this(...args);
        ctrl.createRoot();
        return ctrl;
    }
}

export abstract class ScrollableCompositeControl extends BaseCompositeControl {
    protected readonly scrollPanel: ScrollablePanel;

    protected constructor(create: () => void, vScroll: boolean, hScroll: boolean) {
        super(create, vScroll, hScroll);
        this.scrollPanel = new ScrollablePanel({ hScroll, vScroll });
        this.setOuter(this.scrollPanel);
    }

    protected createRoot(): void {
        this.root = new GridLayout(this.initLayout());
        this.scrollPanel.append(this.root);
    }
}
